import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ChevronDown } from "lucide-react";
import { SITE, NAV_LINKS, waLink } from "@/data/site";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [expOpen, setExpOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); setExpOpen(false); }, [pathname]);

  return (
    <>
      <header
        data-testid="site-navbar"
        className={`fixed inset-x-0 top-0 z-50 border-b border-charcoal/10 bg-ivory/95 backdrop-blur-md transition-all duration-300 ${
          scrolled ? "py-2 shadow-[0_6px_24px_rgba(26,54,38,0.10)]" : "py-3"
        }`}
      >
        <div className="mx-auto flex max-w-[1400px] items-center justify-between px-5 md:px-10">
          <motion.div
            initial={{ opacity: 0, y: -14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
          >
            <Link
              to="/"
              data-testid="nav-logo"
              className="group inline-flex flex-col items-center leading-none"
            >
              <motion.span
                data-testid="nav-brand-name"
                className="uppercase text-[15px] sm:text-lg md:text-xl lg:text-[22px]"
                style={{
                  fontFamily: '"Playfair Display", ui-serif, Georgia, serif',
                  fontWeight: 900,
                  letterSpacing: "0.24em",
                  backgroundImage:
                    "linear-gradient(100deg, #0F2A1D 0%, #1A3626 18%, #B7873B 38%, #FFF1B8 48%, #F7E3A1 50%, #FFF1B8 52%, #B7873B 62%, #1A3626 82%, #0F2A1D 100%)",
                  backgroundSize: "220% 100%",
                  WebkitBackgroundClip: "text",
                  backgroundClip: "text",
                  color: "transparent",
                  WebkitTextFillColor: "transparent",
                  filter:
                    "drop-shadow(0 1px 0 rgba(255,255,255,0.55)) drop-shadow(0 2px 6px rgba(15,42,29,0.28)) drop-shadow(0 0 12px rgba(235,200,117,0.25))",
                }}
                animate={{ backgroundPosition: ["220% 0%", "-120% 0%"] }}
                transition={{ duration: 2.6, ease: "linear", repeat: Infinity, repeatDelay: 0.4 }}
              >
                Vruksha Valley
              </motion.span>
              <span
                data-testid="nav-brand-sub"
                className="mt-1.5 flex items-center gap-2 text-[9px] uppercase sm:text-[10px] md:gap-2.5 md:text-[11px]"
                style={{
                  fontFamily: '"Playfair Display", ui-serif, Georgia, serif',
                  fontWeight: 700,
                  letterSpacing: "0.42em",
                }}
              >
                <span
                  aria-hidden
                  className="block h-[1.5px] w-8 md:w-10"
                  style={{
                    background:
                      "linear-gradient(90deg, rgba(138,106,42,0) 0%, #B7873B 55%, rgba(138,106,42,0) 100%)",
                  }}
                />
                <motion.span
                  className="tracking-[0.42em]"
                  style={{
                    backgroundImage:
                      "linear-gradient(100deg, #8A6A2A 0%, #B7873B 30%, #FFF1B8 48%, #F7E3A1 50%, #FFF1B8 52%, #B7873B 70%, #8A6A2A 100%)",
                    backgroundSize: "220% 100%",
                    WebkitBackgroundClip: "text",
                    backgroundClip: "text",
                    color: "transparent",
                    WebkitTextFillColor: "transparent",
                    filter: "drop-shadow(0 0 6px rgba(235,200,117,0.35))",
                  }}
                  animate={{ backgroundPosition: ["220% 0%", "-120% 0%"] }}
                  transition={{ duration: 2.6, ease: "linear", repeat: Infinity, repeatDelay: 0.4, delay: 0.35 }}
                >
                  Resort
                </motion.span>
                <span
                  aria-hidden
                  className="block h-[1.5px] w-8 md:w-10"
                  style={{
                    background:
                      "linear-gradient(90deg, rgba(138,106,42,0) 0%, #B7873B 45%, rgba(138,106,42,0) 100%)",
                  }}
                />
              </span>
            </Link>
          </motion.div>

          <nav className="hidden items-center gap-7 lg:flex">
            {NAV_LINKS.map((l) =>
              l.dropdown ? (
                <div key={l.label} className="relative" onMouseEnter={() => setExpOpen(true)} onMouseLeave={() => setExpOpen(false)}>
                  <button data-testid="nav-experiences" className="flex items-center gap-1 text-sm text-charcoal/80 transition-colors hover:text-forest">
                    {l.label} <ChevronDown className="h-3.5 w-3.5" />
                  </button>
                  <AnimatePresence>
                    {expOpen && (
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 10 }}
                        transition={{ duration: 0.2 }}
                        className="absolute left-1/2 top-full w-60 -translate-x-1/2 pt-4"
                      >
                        <div className="overflow-hidden rounded-2xl border border-charcoal/10 bg-ivory p-2 shadow-[0_20px_50px_rgba(26,54,38,0.15)]">
                          {l.dropdown.map((d) => (
                            <Link key={d.to} to={d.to} data-testid={`nav-dropdown-${d.to.split("/").pop()}`} className="block rounded-xl px-4 py-2.5 text-sm text-charcoal/80 transition-colors hover:bg-moss/10 hover:text-forest">
                              {d.label}
                            </Link>
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ) : (
                <NavLink
                  key={l.to}
                  to={l.to}
                  data-testid={`nav-link-${l.label.toLowerCase()}`}
                  className={({ isActive }) =>
                    `relative text-sm transition-colors after:absolute after:-bottom-1.5 after:left-0 after:h-px after:bg-forest after:transition-all after:duration-300 ${isActive ? "text-forest after:w-full" : "text-charcoal/80 hover:text-forest after:w-0 hover:after:w-full"}`
                  }
                >
                  {l.label}
                </NavLink>
              )
            )}
            <a
              data-testid="nav-book-btn"
              href={waLink()}
              target="_blank"
              rel="noopener noreferrer"
              className="rounded-full bg-forest px-5 py-2 text-sm font-medium text-ivory transition-all duration-300 hover:-translate-y-0.5 hover:bg-moss"
            >
              Book Your Stay
            </a>
          </nav>

          <button data-testid="mobile-menu-toggle" onClick={() => setOpen(true)} aria-label="Open menu" className="text-forest lg:hidden">
            <Menu className="h-7 w-7" />
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div data-testid="mobile-menu" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.35 }} className="fixed inset-0 z-[70] overflow-y-auto bg-forest text-ivory">
            <div className="flex items-center justify-between px-5 py-4">
              <div className="inline-flex flex-col leading-none">
                <span
                  data-testid="mobile-brand-name"
                  className="uppercase text-lg"
                  style={{
                    fontFamily: '"Playfair Display", ui-serif, Georgia, serif',
                    fontWeight: 900,
                    letterSpacing: "0.24em",
                    backgroundImage:
                      "linear-gradient(90deg, #F5E9C8 0%, #F7EFD5 40%, #E4C67A 70%, #B7873B 100%)",
                    WebkitBackgroundClip: "text",
                    backgroundClip: "text",
                    color: "transparent",
                    WebkitTextFillColor: "transparent",
                  }}
                >
                  Vruksha Valley
                </span>
                <span
                  className="mt-1.5 flex items-center gap-2 text-[10px] uppercase"
                  style={{
                    fontFamily: '"Playfair Display", ui-serif, Georgia, serif',
                    fontWeight: 700,
                    letterSpacing: "0.42em",
                    color: "#E4C67A",
                  }}
                >
                  <span
                    aria-hidden
                    className="block h-px w-8"
                    style={{
                      background:
                        "linear-gradient(90deg, rgba(228,198,122,0) 0%, #E4C67A 55%, rgba(228,198,122,0) 100%)",
                    }}
                  />
                  <span className="tracking-[0.42em]">Resort</span>
                  <span
                    aria-hidden
                    className="block h-px w-8"
                    style={{
                      background:
                        "linear-gradient(90deg, rgba(228,198,122,0) 0%, #E4C67A 45%, rgba(228,198,122,0) 100%)",
                    }}
                  />
                </span>
              </div>
              <button data-testid="mobile-menu-close" onClick={() => setOpen(false)} aria-label="Close menu"><X className="h-7 w-7" /></button>
            </div>
            <nav className="mt-4 flex flex-col px-6 pb-10">
              {NAV_LINKS.map((l, i) => (
                <motion.div key={l.label} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.06 + i * 0.05, duration: 0.4 }}>
                  {l.dropdown ? (
                    <div className="border-b border-ivory/15 py-3">
                      <p className="mb-1 font-display text-3xl">{l.label}</p>
                      <div className="flex flex-col gap-1 pl-1">
                        {l.dropdown.map((d) => (
                          <Link key={d.to} to={d.to} data-testid={`mobile-dropdown-${d.to.split("/").pop()}`} className="py-1.5 text-ivory/75">{d.label}</Link>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <Link to={l.to} data-testid={`mobile-nav-${l.label.toLowerCase()}`} className="block border-b border-ivory/15 py-3.5 font-display text-3xl">{l.label}</Link>
                  )}
                </motion.div>
              ))}
              <a href={waLink()} target="_blank" rel="noopener noreferrer" className="mt-7 rounded-full bg-champagne px-6 py-4 text-center font-medium text-forest">Book Your Stay on WhatsApp</a>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
